/* ============================================================================
   Cart Drawer Upsell
   ----------------------------------------------------------------------------
   Custom element used by snippets/cart-upsell.liquid.
   Reads two JSON payloads from its children (pinned product + extras list)
   and renders a pinned card + one rotating "primary" card, plus a "Show more"
   reveal for the rest. Clicking a card with variants opens an inline picker;
   confirming POSTs to /cart/add.js and re-renders the cart drawer.
   ============================================================================ */

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this._busy = false;
  }

  connectedCallback() {
    // Localized UI strings (see window.beastCartUpsellStrings in theme.liquid
    // <head>); fall back to English so the component still works if it's missing.
    this.S = window.beastCartUpsellStrings || {};

    try {
      const pinnedRaw = this.querySelector('[data-upsell-pinned]')?.textContent?.trim();
      const extrasRaw = this.querySelector('[data-upsell-extras-data]')?.textContent?.trim();
      this.pinned = pinnedRaw ? JSON.parse(pinnedRaw) : null;
      this.extras = extrasRaw ? JSON.parse(extrasRaw) : [];
    } catch (e) {
      console.error('[CartUpsell] Failed to parse product data', e);
      return;
    }

    if (!this.pinned && this.extras.length === 0) {
      this.style.display = 'none';
      return;
    }

    // Tag shown on the pinned card. Defaults to "Champions Edition"; an empty
    // data-pinned-tag (e.g. the Barcelona rules) hides it.
    this.pinnedTag = this.hasAttribute('data-pinned-tag')
      ? this.getAttribute('data-pinned-tag')
      : 'Champions Edition';

    this.grid = this.querySelector('[data-upsell-grid]');
    this.extrasEl = this.querySelector('[data-upsell-extras]');
    this.moreBtn = this.querySelector('[data-upsell-more]');
    this.moreLabel = this.querySelector('[data-upsell-more-label]');
    this.moreCount = this.querySelector('[data-upsell-more-count]');

    this._renderTop();
    this._bindMore();
    this._fitBarText();
    this._fitCardPrices();

    this._onResize = () => {
      this._fitBarText();
      this._fitCardPrices();
    };
    window.addEventListener('resize', this._onResize);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._cartUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
        if (e && e.source === 'cart-upsell') return;
        this._refreshFromServer();
      });
    }
  }

  disconnectedCallback() {
    if (this._cartUnsub) this._cartUnsub();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  _fitBarText() {
    const bar = this.querySelector('.cart-upsell__bar');
    if (!bar) return;
    const visible = bar.querySelector('[data-bar-pre]:not([hidden]), [data-bar-post]:not([hidden])');
    if (!visible) return;

    bar.style.fontSize = '';

    requestAnimationFrame(() => {
      const required = visible.scrollWidth;
      const available = visible.clientWidth;
      if (required <= available || available <= 0) return;

      const baseSize = parseFloat(window.getComputedStyle(bar).fontSize) || 10;
      const minSize = 8;
      const targetSize = Math.max(baseSize * (available / required) * 0.98, minSize);
      bar.style.fontSize = targetSize + 'px';
    });
  }

  _fitCardPrices() {
    const rows = this.querySelectorAll('.cu-card__price-row');
    rows.forEach((row) => {
      const price = row.querySelector('.cu-card__price');
      const priceWas = row.querySelector('.cu-card__price-was');
      if (!price) return;

      price.style.fontSize = '';
      if (priceWas) priceWas.style.fontSize = '';

      requestAnimationFrame(() => {
        const originalWrap = row.style.flexWrap;
        row.style.flexWrap = 'nowrap';
        const required = row.scrollWidth;
        const available = row.clientWidth;
        row.style.flexWrap = originalWrap;

        if (required <= available || available <= 0) return;

        const ratio = (available / required) * 0.98;

        const basePrice = parseFloat(window.getComputedStyle(price).fontSize) || 12.5;
        const newPrice = Math.max(basePrice * ratio, 10);
        price.style.fontSize = newPrice + 'px';

        if (priceWas) {
          const baseWas = parseFloat(window.getComputedStyle(priceWas).fontSize) || 10.5;
          const newWas = Math.max(baseWas * ratio, 9);
          priceWas.style.fontSize = newWas + 'px';
        }
      });
    });
  }

  async _refreshFromServer() {
    try {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const resp = await fetch(`${cartUrl}?section_id=cart-drawer`);
      const text = await resp.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const source = parsed.querySelector('cart-upsell');
      if (source) {
        this.replaceWith(source);
      } else {
        this.remove();
      }
    } catch (e) {
      console.error('[CartUpsell] refresh failed', e);
    }
  }

  /* ── Top grid: pinned + a "primary" rotating pick ────────────────────── */

  _renderTop() {
    const TOP_SLOTS = 2;
    let remaining = this.extras.slice();
    const topCards = [];

    if (this.pinned) {
      topCards.push({ product: this.pinned, opts: { pinned: true } });
    }

    while (topCards.length < TOP_SLOTS && remaining.length > 0) {
      const idx = Math.floor(Math.random() * remaining.length);
      topCards.push({ product: remaining[idx], opts: {} });
      remaining = remaining.filter((_, j) => j !== idx);
    }

    this._remaining = remaining;

    this.grid.innerHTML = topCards.map((t) => this._cardHTML(t.product, t.opts)).join('');

    const cards = this.grid.querySelectorAll('.cu-card');
    cards.forEach((card, i) => this._bindCard(card, topCards[i].product));

    if (this.moreCount) {
      this.moreCount.textContent = remaining.length > 0 ? `(${remaining.length})` : '';
    }
    if (this.moreBtn) {
      this.moreBtn.hidden = remaining.length === 0;
    }
  }

  /* ── Show more / less ─────────────────────────────────────────────────── */

  _bindMore() {
    if (!this.moreBtn) return;
    this._initialMoreLabel = this.moreLabel ? this.moreLabel.textContent : 'Show more';
    this.moreBtn.addEventListener('click', () => {
      const isOpen = this.moreBtn.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.extrasEl.hidden = true;
        this.moreBtn.setAttribute('aria-expanded', 'false');
        if (this.moreLabel) this.moreLabel.textContent = this._initialMoreLabel;
        if (this.moreCount) this.moreCount.textContent = this._remaining.length > 0 ? `(${this._remaining.length})` : '';
      } else {
        if (!this.extrasEl.dataset.rendered) {
          this.extrasEl.innerHTML = this._remaining.map((p) => this._cardHTML(p)).join('');
          this.extrasEl.querySelectorAll('.cu-card').forEach((card, i) => {
            this._bindCard(card, this._remaining[i]);
          });
          this.extrasEl.dataset.rendered = '1';
        }
        this.extrasEl.hidden = false;
        this.moreBtn.setAttribute('aria-expanded', 'true');
        if (this.moreLabel) this.moreLabel.textContent = this.S.showLess || 'Show less';
        if (this.moreCount) this.moreCount.textContent = '';
      }
      requestAnimationFrame(() => {
        this._fitBarText();
        this._fitCardPrices();
      });
    });
  }

  /* ── Card HTML ────────────────────────────────────────────────────────── */

  _cardHTML(product, opts = {}) {
    const pinned = !!opts.pinned;
    const tag = pinned && this.pinnedTag ? `<span class="cu-card__tag">${this._esc(this.pinnedTag)}</span>` : '';
    const img = product.image
      ? `<img src="${product.image}" alt="${this._esc(product.title)}" loading="lazy" width="200" height="150">`
      : `<span class="cu-card__img-placeholder">${this._esc(product.title)}</span>`;
    const priceWas =
      product.compare_at_price && product.compare_at_price !== product.price
        ? `<span class="cu-card__price-was">${product.compare_at_price}</span>`
        : '';
    const hasOptions = (product.options || []).some((o) => o.values && o.values.length > 1);
    const variantPrices = (product.variants || []).map((v) => v.price);
    const hasPriceRange = variantPrices.length > 1 && new Set(variantPrices).size > 1;
    const ctaLabel = `<span class="cu-card__cta-plus">+</span> ${this.S.addToBundle || 'Add to bundle'}`;
    const fromLabel = hasPriceRange ? (this.S.fromPrice || 'From ') : '';

    return `
      <div class="cu-card ${pinned ? 'cu-card--pinned' : ''}" data-handle="${this._esc(product.handle)}">
        ${tag}
        <div class="cu-card__img">${img}</div>
        <div class="cu-card__body">
          <h4 class="cu-card__name">${this._esc(product.title)}</h4>
          <div class="cu-card__price-row">
            <span class="cu-card__price">${fromLabel}${product.price}</span>
            ${priceWas}
          </div>
          <button class="cu-card__cta" type="button" data-cu-add>${ctaLabel}</button>
        </div>
        <div class="cu-picker" aria-hidden="true"></div>
      </div>
    `;
  }

  _bindCard(card, product) {
    const cta = card.querySelector('[data-cu-add]');
    if (!cta) return;
    cta.addEventListener('click', () => this._handleAdd(card, product));
  }

  _handleAdd(card, product) {
    const requiresPicker = (product.options || []).some((o) => o.values && o.values.length > 1);
    if (!requiresPicker) {
      const variant = (product.variants || []).find((v) => v.available);
      if (!variant) return;
      this._addVariant(card, product, variant);
      return;
    }
    card.classList.add('is-picking');
    this._renderPicker(card, product);
  }

  /* ── Variant picker ──────────────────────────────────────────────────── */

  _renderPicker(card, product) {
    const picker = card.querySelector('.cu-picker');
    const selected = product.options.map(() => null);

    const draw = () => {
      const matched = this._findVariant(product, selected);
      const allChosen = selected.every((v) => v !== null);
      const confirmDisabled = !allChosen || !matched || !matched.available;
      const confirmPrice = matched ? matched.price : product.price;

      const groupsHTML = product.options.map((opt, oi) => {
        const chipsHTML = opt.values
          .map((val) => {
            const available = this._isValueAvailable(product, oi, val, selected);
            const isSelected = selected[oi] === val;
            return `<button type="button"
              class="cu-chip ${isSelected ? 'is-selected' : ''} ${!available ? 'is-sold-out' : ''}"
              ${!available ? 'disabled' : ''}
              data-option-index="${oi}"
              data-value="${this._esc(val)}">${this._esc(val)}</button>`;
          })
          .join('');
        return `
          <div class="cu-picker__group">
            <div class="cu-picker__label">
              <span>${this._esc(opt.name)}</span>
              <span class="cu-picker__label-val">${selected[oi] ? this._esc(selected[oi]) : (this.S.select || 'Select')}</span>
            </div>
            <div class="cu-chips">${chipsHTML}</div>
          </div>
        `;
      }).join('');

      const confirmText = confirmDisabled && allChosen
        ? (this.S.soldOut || 'Sold out')
        : (this.S.addFor || 'Add for [price]').replace('[price]', confirmPrice);

      picker.innerHTML = `
        ${groupsHTML}
        <div class="cu-picker__footer">
          <button type="button" class="cu-picker__cancel" data-cu-cancel>${this.S.back || 'Back'}</button>
          <button type="button" class="cu-picker__confirm" data-cu-confirm ${confirmDisabled ? 'disabled' : ''}>
            ${confirmText}
          </button>
        </div>
      `;

      picker.querySelectorAll('.cu-chip').forEach((chip) => {
        if (chip.disabled) return;
        chip.addEventListener('click', () => {
          const oi = +chip.dataset.optionIndex;
          selected[oi] = chip.dataset.value;
          draw();
        });
      });

      picker.querySelector('[data-cu-cancel]').addEventListener('click', () => {
        card.classList.remove('is-picking');
        picker.innerHTML = '';
      });

      const confirmBtn = picker.querySelector('[data-cu-confirm]');
      if (!confirmBtn.disabled) {
        confirmBtn.addEventListener('click', () => {
          const v = this._findVariant(product, selected);
          if (v && v.available) this._addVariant(card, product, v);
        });
      }
    };

    draw();
  }

  _findVariant(product, selected) {
    return (product.variants || []).find(
      (v) => v.options.length === selected.length && v.options.every((opt, i) => opt === selected[i])
    );
  }

  _isValueAvailable(product, optionIndex, value, selected) {
    return (product.variants || []).some((v) => {
      if (!v.available) return false;
      if (v.options[optionIndex] !== value) return false;
      return v.options.every((opt, i) => {
        if (i === optionIndex) return true;
        return selected[i] == null || selected[i] === opt;
      });
    });
  }

  /* ── Cart hook ───────────────────────────────────────────────────────── */

  async _addVariant(card, product, variant) {
    if (this._busy) return;
    this._busy = true;

    const cta = card.querySelector('[data-cu-add]');
    const confirm = card.querySelector('[data-cu-confirm]');
    if (cta) cta.disabled = true;
    if (confirm) confirm.disabled = true;

    try {
      const addResp = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const data = await addResp.json();
      if (data.status) {
        console.error('[CartUpsell] /cart/add.js error:', data);
        if (cta) cta.disabled = false;
        if (confirm) confirm.disabled = false;
        this._busy = false;
        return;
      }

      this._showAddedState(card, product);

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const cartResp = await fetch(`${cartUrl}?section_id=cart-drawer`);
        const cartText = await cartResp.text();
        const parsed = new DOMParser().parseFromString(cartText, 'text/html');
        for (const sel of ['cart-drawer-items', '.cart-drawer__footer', '.cart__ctas']) {
          const target = document.querySelector(sel);
          const source = parsed.querySelector(sel);
          if (target && source) target.replaceWith(source);
        }
        const upsellSource = parsed.querySelector('cart-upsell');
        const upsellCurrent = document.querySelector('cart-upsell');
        if (upsellCurrent) {
          if (upsellSource) {
            upsellCurrent.replaceWith(upsellSource);
          } else {
            upsellCurrent.remove();
          }
        }
        drawer.classList.remove('is-empty');
        const innerEl = drawer.querySelector('.drawer__inner');
        if (innerEl) innerEl.classList.remove('is-empty');
      }

      fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}?section_id=cart-icon-bubble`)
        .then((r) => r.text())
        .then((html) => {
          const bubbleParsed = new DOMParser().parseFromString(html, 'text/html');
          const el = document.getElementById('cart-icon-bubble');
          const source = bubbleParsed.querySelector('.shopify-section');
          if (el && source) el.innerHTML = source.innerHTML;
        })
        .catch(() => {});
    } catch (e) {
      console.error('[CartUpsell] Add to cart failed:', e);
      if (cta) cta.disabled = false;
      if (confirm) confirm.disabled = false;
    } finally {
      this._busy = false;
    }
  }

  _showAddedState(card, product) {
    card.classList.remove('is-picking');
    const isPinned = card.classList.contains('cu-card--pinned');
    const addedLabel = this.S.addedTag || 'Added';
    const tag = isPinned
      ? `<span class="cu-card__tag">${addedLabel}</span>`
      : `<span class="cu-card__tag" style="background:#b90000;color:#fff;">${addedLabel}</span>`;
    const img = product.image
      ? `<img src="${product.image}" alt="${this._esc(product.title)}" loading="lazy">`
      : `<span class="cu-card__img-placeholder">${this._esc(product.title)}</span>`;
    card.innerHTML = `
      ${tag}
      <div class="cu-card__img">${img}</div>
      <div class="cu-card__added">✓ ${this.S.addedToBundle || 'Added to bundle'}</div>
    `;
    const fill = this.querySelector('.cart-upsell__progress-fill');
    if (fill) {
      fill.style.width = '100%';
      fill.classList.add('is-complete');
    }
    const bar = this.querySelector('.cart-upsell__bar');
    if (bar) bar.classList.add('is-complete');
    const barPre = this.querySelector('[data-bar-pre]');
    const barPost = this.querySelector('[data-bar-post]');
    if (barPre) barPre.hidden = true;
    if (barPost) barPost.hidden = false;
    this._fitBarText();
  }

  /* ── Utils ───────────────────────────────────────────────────────────── */

  _esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
